import React, { useState, useEffect, useRef } from 'react';

export interface BalloonProps {
  minSize: number; // Percentual da altura da tela (ex: 10 para 10%)
  maxSize: number; // Percentual da altura da tela (ex: 30 para 30%)
  maxOpacity?: number; // Opacidade máxima (0-100, padrão 100)
  liveTime: number; // Tempo total em segundos
  position: { x: number; y: number }; // Posição em pixels
  imageSrc: string; // Caminho da imagem do balão
  onDestroy?: () => void; // Callback quando o balão for destruído
  transitionDuration?: number; // Duração da transição em segundos (padrão 0.1s)
  noFade?: boolean; // Se true, não aplica animação de fade (aparece/desaparece instantaneamente)
}

const Balloon: React.FC<BalloonProps> = ({
  minSize,
  maxSize,
  maxOpacity = 100,
  liveTime,
  position,
  imageSrc,
  onDestroy,
  transitionDuration = 0.1, // Valor padrão de 0.1s para não afetar aplicações existentes
  noFade = false // Valor padrão false para manter comportamento atual
}) => {
  const [opacity, setOpacity] = useState(0);
  const [isDestroyed, setIsDestroyed] = useState(false);
  const animationRef = useRef<number>();
  const startTimeRef = useRef<number>(0);
  const balloonSize = useRef<number>(0);

  // Calcular tamanho aleatório baseado na altura da tela
  useEffect(() => {
    const randomSizePercentage = minSize + Math.random() * (maxSize - minSize);
    balloonSize.current = Math.round((randomSizePercentage / 100) * window.innerHeight);
  }, [minSize, maxSize]);

  // Função de animação da opacidade
  const animate = (currentTime: number) => {
    if (!startTimeRef.current) {
      startTimeRef.current = currentTime;
    }

    const elapsed = (currentTime - startTimeRef.current) / 1000; // Converter para segundos
    const progress = elapsed / liveTime; // Progresso de 0 a 1

    if (progress >= 1) {
      // Tempo acabou, destruir o balão
      setOpacity(0);
      setIsDestroyed(true);
      if (onDestroy) {
        onDestroy();
      }
      return;
    }

    // Se noFade=true, manter opacidade máxima durante toda a vida
    if (noFade) {
      setOpacity(maxOpacity / 100);
      animationRef.current = requestAnimationFrame(animate);
      return;
    }

    // Calcular opacidade usando uma função senoidal
    // Para oscilar de 0 -> maxOpacity -> 0
    let currentOpacity;
    if (progress <= 0.5) {
      // Primeira metade: 0 -> maxOpacity
      currentOpacity = (maxOpacity / 100) * (progress * 2);
    } else {
      // Segunda metade: maxOpacity -> 0
      currentOpacity = (maxOpacity / 100) * (2 - progress * 2);
    }

    setOpacity(currentOpacity);
    animationRef.current = requestAnimationFrame(animate);
  };

  // Iniciar animação
  useEffect(() => {
    // Se noFade=true, definir opacidade máxima imediatamente
    if (noFade) {
      setOpacity(maxOpacity / 100);
    }
    
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [liveTime, maxOpacity, noFade]);

  // Se foi destruído, não renderizar nada
  if (isDestroyed) {
    return null;
  }

  return (
    <div
      style={{
        position: 'absolute',
        left: position.x - balloonSize.current / 2, // Centralizar horizontalmente
        top: position.y - balloonSize.current / 2, // Centralizar verticalmente
        width: balloonSize.current,
        height: balloonSize.current,
        opacity: opacity,
        pointerEvents: 'none', // Não interferir com cliques
        zIndex: 800, // Acima das fotos flutuantes
        transition: `opacity ${transitionDuration}s ease-out`, // Usar o valor configurável
      }}
    >
      <img
        src={imageSrc}
        alt="Balloon"
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain', // Manter proporções da imagem
          objectPosition: 'center'
        }}
        onError={() => {
          console.error(`Erro ao carregar imagem do balão: ${imageSrc}`);
        }}
      />
    </div>
  );
};

export default Balloon;
